import React, { useEffect, useRef, memo } from 'react';
import Modal from '../../../../components/uielements/modal';
import { X } from 'lucide-react';
import { ModalHeader, ModalBody, CloseButton, ContentWrapper } from '../mapView.style';
import { IntlMessages } from '../../../../helper/langHelper';

const OverlayItem = memo(function OverlayItem({ overlay }) {
  return (
    <div className="overlay-item">
      <p>{overlay?.overlayName}</p>
    </div>
  );
});

const OverlaysList = memo(function OverlaysList({ overlays }) {
  if (!overlays || overlays.length === 0) {
    return (
      <p>
        <IntlMessages id="overlay.empty.state" />
      </p>
    );
  }

  return (
    <div className="overlays-list">
      <p>
        <IntlMessages id="overlay.label" /> :
      </p>
      {overlays.map((overlay, index) => (
        <OverlayItem key={`${overlay.id || index}`} overlay={overlay} />
      ))}
    </div>
  );
});

const PropertyOverlaysModal = ({ isModalOpen, onClose, addressOverlays, isFetching }) => {
  const closeButtonRef = useRef(null);
  const modalRef = useRef(null);
  const lastFocusedElement = useRef(null);

  // Handle focus management for accessibility
  useEffect(() => {
    if (isModalOpen) {
      lastFocusedElement.current = document.activeElement;
      setTimeout(() => closeButtonRef.current?.focus(), 100);
    } else if (lastFocusedElement.current) {
      lastFocusedElement.current.focus();
    }
  }, [isModalOpen]);

  const handleKeyDown = (event) => {
    if (!isModalOpen) return;

    if (event.key === 'Escape') {
      onClose();
    }

    if (event.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      onKeyDown={handleKeyDown}
      ref={modalRef}
    >
      <ModalHeader>
        <h3 id="modal-title">
          <IntlMessages id="overlays.title" />
        </h3>
        <CloseButton
          onClick={onClose}
          ref={closeButtonRef}
          aria-label="Close property overlays"
          type="button"
        >
          <X size={24} aria-hidden="true" />
        </CloseButton>
      </ModalHeader>

      <ModalBody id="modal-description">
        <ContentWrapper>
          {isFetching ? (
            <p>
              <IntlMessages id="overlay.fetching.state" />
            </p>
          ) : (
            <OverlaysList overlays={addressOverlays} />
          )}
        </ContentWrapper>
      </ModalBody>
    </Modal>
  );
};

export default PropertyOverlaysModal;
